import { useCallback, useEffect, useRef, useState } from 'react'

const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY_MS = 2000

type UseImageRetryResult = {
  hasError: boolean
  imgSrc: string
  onError: () => void
  onLoad: () => void
}

/**
 * Gracefully handles image load failures with automatic retry using exponential backoff.
 *
 * Retries up to 3 times (delays: 2s, 4s, 8s) by appending a cache-busting `_retry` query
 * parameter. Hides the broken-image icon while retries are in progress.
 *
 * Use this for images served via signed URLs (e.g. CloudFront) that may fail transiently
 * due to timing, propagation delays, or brief network errors.
 */
export const useImageRetry = (src: string): UseImageRetryResult => {
  const [hasError, setHasError] = useState(false)
  const [retrySrc, setRetrySrc] = useState('')
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      clearTimeout(retryTimerRef.current)
    }
  }, [])

  useEffect(() => {
    clearTimeout(retryTimerRef.current)
    retryCountRef.current = 0
    setRetrySrc('')
    setHasError(false)
  }, [src])

  const onLoad = useCallback(() => {
    retryCountRef.current = 0
    setHasError(false)
  }, [])

  // onError fires once per src change (HTML spec), and retryCountRef guards against exceeding MAX_RETRIES
  const onError = useCallback(() => {
    setHasError(true)
    if (retryCountRef.current < MAX_RETRIES) {
      const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current)
      retryCountRef.current += 1
      retryTimerRef.current = setTimeout(() => {
        const separator = src.includes('?') ? '&' : '?'
        setRetrySrc(`${src}${separator}_retry=${retryCountRef.current}`)
      }, delay)
    } else {
      console.warn(`[useImageRetry] Failed to load image after ${MAX_RETRIES} retries: ${src}`)
    }
  }, [src])

  return {
    hasError,
    imgSrc: retrySrc || src,
    onError,
    onLoad,
  }
}
